import {
  useEditor, useLayer, useSelection, usePlayhead, useResolvedLayer,
  UPDATE_PROPERTY, ADD_EFFECT, REMOVE_EFFECT, ADD_KEYFRAME, REMOVE_KEYFRAME, SET_PLAYHEAD,
  snapToFrame, formatTimecode,
} from '@/lib/kinema';
import type { BaseLayerData } from '@/lib/kinema';
import { FontPicker } from './FontPicker';
import { fetchFontFamilies, getFontFamily, loadFont } from '../fonts';
import { useEditorActions } from '../shell/editorActionsContext';
import { useTranscode } from '../transcodeContext';
import { useAssetKit, type AssetKitFont, type AssetKitTypographyStyle } from '../assetKit';
import { Fragment, useEffect, useRef, useState } from 'react';
import { MediaLibraryModal } from './MediaLibraryModal';
import AdTokenInput from '@/shared/adTokens/AdTokenInput';
import AdTokenMessages from '@/shared/adTokens/AdTokenMessages';
import { useAdTokenCheck } from '@/shared/adTokens/useAdTokenCheck';
import type { AdTokenFieldHandle } from '@/shared/adTokens/useAdTokenPicker';
import {
  adTokenSources,
  fixAdTokenAlias,
  useAdTokenFindings,
  type AdTokenFocusRequest,
  type AdTokenSource,
} from '../adTokenFindings';

// ─── Property group definitions ───────────────────────────

interface PropDef {
  name: string;
  label: string;
  step?: number;
  options?: string[];
  type?: 'font' | 'bolditalic' | 'hidden' | 'media' | 'resetDimensions' | 'color' | 'boolean';
}

interface PropGroup {
  label: string;
  props: PropDef[];
}

const TRANSFORM_GROUPS: PropGroup[] = [
  { label: 'Anchor Point', props: [{ name: 'anchorX', label: 'X' }, { name: 'anchorY', label: 'Y' }] },
  { label: 'Position', props: [{ name: 'x', label: 'X' }, { name: 'y', label: 'Y' }] },
  { label: 'Scale', props: [{ name: 'scaleX', label: 'X', step: 0.01 }, { name: 'scaleY', label: 'Y', step: 0.01 }] },
  { label: 'Rotation', props: [{ name: 'rotation', label: '', step: 1 }] },
  { label: 'Opacity', props: [{ name: 'opacity', label: '', step: 0.01 }] },
];

const SHADOW_GROUPS: PropGroup[] = [
  { label: 'Color', props: [{ name: 'shadowColor', label: '', type: 'color' }] },
  { label: 'Blur', props: [{ name: 'shadowBlur', label: '', step: 1 }] },
  { label: 'Offset', props: [{ name: 'shadowOffsetX', label: 'X', step: 1 }, { name: 'shadowOffsetY', label: 'Y', step: 1 }] },
  { label: 'Opacity', props: [{ name: 'shadowOpacity', label: '', step: 0.01 }] },
];

const TYPE_GROUPS: Record<string, PropGroup[]> = {
  shape: [
    { label: 'Fill', props: [{ name: 'fill', label: '' }] },
    { label: 'Stroke', props: [{ name: 'stroke', label: '' }, { name: 'strokeWidth', label: 'Width', step: 1 }] },
    { label: 'Corner Radius', props: [{ name: 'cornerRadius', label: '', step: 1 }] },
    { label: 'Path', props: [{ name: 'pathPoints', label: '', type: 'hidden' }] },
    { label: 'Trim', props: [{ name: 'trimStart', label: 'Start', step: 0.01 }, { name: 'trimEnd', label: 'End', step: 0.01 }] },
  ],
  text: [
    { label: 'Text', props: [{ name: 'text', label: '' }] },
    { label: 'Font', props: [{ name: 'fontFamily', label: '', type: 'font' }] },
    { label: 'Style', props: [{ name: 'fontWeight', label: '', type: 'bolditalic' }] },
    { label: 'Font Size', props: [{ name: 'fontSize', label: '', step: 1 }] },
    { label: 'Color', props: [{ name: 'color', label: '' }] },
    { label: 'Align', props: [{ name: 'textAlign', label: '', options: ['left', 'center', 'right'] }] },
    { label: 'V. Align', props: [{ name: 'verticalAlign', label: '', options: ['top', 'middle', 'bottom'] }] },
    { label: 'Line Height', props: [{ name: 'lineHeight', label: '', step: 0.1 }] },
  ],
  image: [
    { label: 'Source', props: [{ name: 'src', label: '', type: 'media' }] },
    { label: '', props: [{ name: '_resetDimensions', label: '', type: 'resetDimensions' }] },
  ],
  video: [
    { label: 'Source', props: [{ name: 'src', label: '', type: 'media' }] },
    { label: 'Volume', props: [{ name: 'volume', label: '', step: 0.01 }] },
    { label: 'Trim', props: [{ name: 'trimStart', label: 'Start', step: 0.01 }, { name: 'trimEnd', label: 'End', step: 0.01 }] },
    { label: 'Loop', props: [{ name: 'loop', label: '', type: 'boolean' }] },
    { label: '', props: [{ name: '_resetDimensions', label: '', type: 'resetDimensions' }] },
  ],
  audio: [
    { label: 'Source', props: [{ name: 'src', label: '', type: 'media' }] },
    { label: 'Volume', props: [{ name: 'volume', label: '', step: 0.01 }] },
    { label: 'Trim', props: [{ name: 'trimStart', label: 'Start', step: 0.01 }, { name: 'trimEnd', label: 'End', step: 0.01 }] },
    { label: 'Loop', props: [{ name: 'loop', label: '', type: 'boolean' }] },
  ],
};

// ─── Main Panel ───────────────────────────────────────────

export function PropertiesPanel() {
  const editor = useEditor();
  const { primary } = useSelection();
  const layer = useLayer(primary);
  const resolved = useResolvedLayer(primary);
  const { time, frameRate } = usePlayhead();
  const { enabled: adTokensOn } = useAdTokenFindings();
  if (!layer) {
    return (
      <div style={{ ...panelStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 2L15 8.5L8.5 10L7 15.5L2 2Z" />
          <path d="M15 8.5L17 17L8.5 10" />
        </svg>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>Select a layer to edit</span>
      </div>
    );
  }

  const updateProp = (property: string, value: any) => {
    editor.dispatchCommand(UPDATE_PROPERTY, { layerId: layer.id, property, value });
  };

  const isKeyframed = (propNames: string[]) =>
    propNames.some((p) => layer.effects[p] && layer.effects[p].enabled);

  const KF_EPS = 1e-6;
  const localTime = time - layer.startTime;

  const enableKeyframing = (propNames: string[]) => {
    const snappedTime = snapToFrame(localTime, frameRate);
    editor.batch(() => {
      for (const prop of propNames) {
        editor.dispatchCommand(ADD_EFFECT, {
          layerId: layer.id,
          property: prop,
          keyframes: [{ time: snappedTime, value: (layer as any)[prop], easing: 'linear' as const }],
        });
      }
    });
  };

  const disableKeyframing = (propNames: string[]) => {
    editor.batch(() => {
      for (const prop of propNames) {
        if (layer.effects[prop]) {
          editor.dispatchCommand(REMOVE_EFFECT, { layerId: layer.id, property: prop });
        }
      }
    });
  };

  const addKeyframeAtPlayhead = (propNames: string[]) => {
    const snappedTime = snapToFrame(localTime, frameRate);
    editor.batch(() => {
      for (const prop of propNames) {
        const effect = layer.effects[prop];
        const value = resolved ? (resolved as any)[prop] : (layer as any)[prop];
        if (!effect) {
          editor.dispatchCommand(ADD_EFFECT, {
            layerId: layer.id,
            property: prop,
            keyframes: [{ time: snappedTime, value, easing: 'linear' as const }],
          });
          continue;
        }
        const exists = effect.keyframes.some((k) => Math.abs(k.time - snappedTime) < KF_EPS);
        if (exists) continue;
        editor.dispatchCommand(ADD_KEYFRAME, {
          layerId: layer.id,
          property: prop,
          keyframe: { time: snappedTime, value, easing: 'linear' as const },
        });
      }
    });
  };

  const findAdjacentKeyframe = (propNames: string[], direction: 'prev' | 'next'): number | null => {
    let best: number | null = null;
    for (const prop of propNames) {
      const effect = layer.effects[prop];
      if (!effect) continue;
      for (const kf of effect.keyframes) {
        if (direction === 'prev') {
          if (kf.time < localTime - KF_EPS && (best === null || kf.time > best)) best = kf.time;
        } else {
          if (kf.time > localTime + KF_EPS && (best === null || kf.time < best)) best = kf.time;
        }
      }
    }
    return best;
  };

  const seekToAdjacentKeyframe = (propNames: string[], direction: 'prev' | 'next') => {
    const target = findAdjacentKeyframe(propNames, direction);
    if (target === null) return;
    editor.dispatchCommand(SET_PLAYHEAD, { time: layer.startTime + target });
  };

  // Returns true when every keyframed prop in the group has a keyframe at the snapped playhead time.
  // Props without an effect at all are ignored — the diamond reflects "are we sitting on a keyframe".
  const isOnKeyframe = (propNames: string[]) => {
    const snappedTime = snapToFrame(localTime, frameRate);
    const keyframed = propNames.filter((p) => layer.effects[p]);
    if (keyframed.length === 0) return false;
    return keyframed.every((p) =>
      layer.effects[p].keyframes.some((k) => Math.abs(k.time - snappedTime) < KF_EPS),
    );
  };

  const removeKeyframeAtPlayhead = (propNames: string[]) => {
    const snappedTime = snapToFrame(localTime, frameRate);
    editor.batch(() => {
      for (const prop of propNames) {
        const effect = layer.effects[prop];
        if (!effect) continue;
        const kf = effect.keyframes.find((k) => Math.abs(k.time - snappedTime) < KF_EPS);
        if (!kf) continue;
        editor.dispatchCommand(REMOVE_KEYFRAME, {
          layerId: layer.id,
          property: prop,
          time: kf.time,
        });
      }
    });
  };

  const typeGroups = TYPE_GROUPS[layer.type] ?? [];

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <span style={{ flex: 1 }}>Properties</span>
        <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10, textTransform: 'none' }}>
          {formatTimecode(time, frameRate)}
        </span>
      </div>

      {/* Layer name */}
      <div style={{ padding: '6px 12px', borderBottom: '1px solid #f1f5f9' }}>
        {/* The ad token picker on `[` (MPN-1933). Plain with the Studio's ad tokens flagged
            off (MPN-2014). */}
        {adTokensOn ? (
          <LayerNameField layer={layer} onChange={(v) => updateProp('name', v)} />
        ) : (
          <input
            value={layer.name}
            onChange={(e) => updateProp('name', e.target.value)}
            style={{ ...valueInputStyle, fontWeight: 600, fontSize: 13 }}
          />
        )}
      </div>

      {/* Layer Transform */}
      <SectionHeader label="Layer Transform" />
      {TRANSFORM_GROUPS.map((group) => (
        <PropertyRow
          key={group.label}
          group={group}
          layer={layer}
          resolved={resolved}
          onUpdate={updateProp}
          propNames={group.props.map((p) => p.name)}
          isKeyframed={isKeyframed(group.props.map((p) => p.name))}
          onEnableKeyframing={enableKeyframing}
          onDisableKeyframing={disableKeyframing}
          onAddKeyframe={addKeyframeAtPlayhead}
          onRemoveKeyframe={removeKeyframeAtPlayhead}
          isOnKeyframe={isOnKeyframe(group.props.map((p) => p.name))}
          onPrevKeyframe={() => seekToAdjacentKeyframe(group.props.map((p) => p.name), 'prev')}
          onNextKeyframe={() => seekToAdjacentKeyframe(group.props.map((p) => p.name), 'next')}
          hasPrevKeyframe={findAdjacentKeyframe(group.props.map((p) => p.name), 'prev') !== null}
          hasNextKeyframe={findAdjacentKeyframe(group.props.map((p) => p.name), 'next') !== null}
        />
      ))}

      {/* Type-specific properties */}
      {typeGroups.length > 0 && (
        <>
          <SectionHeader label={`${layer.type.charAt(0).toUpperCase() + layer.type.slice(1)} Properties`} />
          {typeGroups.map((group) => {
            // Reset dimensions — render as full-width button, no stopwatch/label
            if (group.props[0]?.type === 'resetDimensions') {
              return (
                <div key="resetDimensions" style={{ padding: '4px 12px' }}>
                  <ResetDimensionsButton layer={layer} onUpdate={updateProp} />
                </div>
              );
            }
            return (
              <Fragment key={group.label}>
                {/* The brand's typography styles, above the font they set (MPN-2033). */}
                {group.props[0]?.type === 'font' && <BrandStyleRow layer={layer} onUpdate={updateProp} />}
                <PropertyRow
                  group={group}
                  layer={layer}
                  resolved={resolved}
                  onUpdate={updateProp}
                  propNames={group.props.map((p) => p.name)}
                  isKeyframed={isKeyframed(group.props.map((p) => p.name))}
                  onEnableKeyframing={enableKeyframing}
                  onDisableKeyframing={disableKeyframing}
                  onAddKeyframe={addKeyframeAtPlayhead}
                  onRemoveKeyframe={removeKeyframeAtPlayhead}
                  isOnKeyframe={isOnKeyframe(group.props.map((p) => p.name))}
                  onPrevKeyframe={() => seekToAdjacentKeyframe(group.props.map((p) => p.name), 'prev')}
                  onNextKeyframe={() => seekToAdjacentKeyframe(group.props.map((p) => p.name), 'next')}
                  hasPrevKeyframe={findAdjacentKeyframe(group.props.map((p) => p.name), 'prev') !== null}
                  hasNextKeyframe={findAdjacentKeyframe(group.props.map((p) => p.name), 'next') !== null}
                />
              </Fragment>
            );
          })}
        </>
      )}

      {/* Drop Shadow */}
      <SectionHeader label="Drop Shadow" />
      {SHADOW_GROUPS.map((group) => (
        <PropertyRow
          key={group.label}
          group={group}
          layer={layer}
          resolved={resolved}
          onUpdate={updateProp}
          propNames={group.props.map((p) => p.name)}
          isKeyframed={isKeyframed(group.props.map((p) => p.name))}
          onEnableKeyframing={enableKeyframing}
          onDisableKeyframing={disableKeyframing}
          onAddKeyframe={addKeyframeAtPlayhead}
          onRemoveKeyframe={removeKeyframeAtPlayhead}
          isOnKeyframe={isOnKeyframe(group.props.map((p) => p.name))}
          onPrevKeyframe={() => seekToAdjacentKeyframe(group.props.map((p) => p.name), 'prev')}
          onNextKeyframe={() => seekToAdjacentKeyframe(group.props.map((p) => p.name), 'next')}
          hasPrevKeyframe={findAdjacentKeyframe(group.props.map((p) => p.name), 'prev') !== null}
          hasNextKeyframe={findAdjacentKeyframe(group.props.map((p) => p.name), 'next') !== null}
        />
      ))}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div style={{
      padding: '6px 12px',
      fontSize: 11,
      fontWeight: 600,
      color: '#475569',
      borderBottom: '1px solid #f1f5f9',
      background: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {label}
    </div>
  );
}

// ─── Font family changes ─────────────────────────────────

type VariantShape = { weight: number; italic: boolean };

/** After a family change: a weight or italic the new family lacks goes back to regular. */
function resetMissingVariant(layer: BaseLayerData, variants: VariantShape[], onUpdate: (prop: string, value: any) => void) {
  const currentWeight = (layer as any).fontWeight ?? 400;
  const currentItalic = (layer as any).fontStyle === 'italic';
  if (currentWeight !== 400 && !variants.some((v) => v.weight === currentWeight)) {
    onUpdate('fontWeight', 400);
  }
  if (currentItalic && !variants.some((v) => v.italic)) {
    onUpdate('fontStyle', 'normal');
  }
}

/** A style's family's variants: the brand's uploaded font files, or the Google catalog's. Undefined when unknown. */
async function styleVariants(style: AssetKitTypographyStyle, brandFonts: AssetKitFont[]): Promise<VariantShape[] | undefined> {
  if (style.source === 'uploaded') {
    const matches = brandFonts.filter((f) => f.family.toLowerCase() === style.family.toLowerCase());
    return matches.length ? matches.map((f) => ({ weight: f.weight ?? 400, italic: f.italic ?? false })) : undefined;
  }
  const family = getFontFamily(style.family)
    ?? (await fetchFontFamilies().catch(() => [])).find((f) => f.family_name === style.family);
  return family?.variants;
}

// ─── Brand typography style (MPN-2033) ───────────────────

/**
 * Applies one of the brand's typography styles: copies its family onto the layer, as picking
 * that family in the Font row would. Nothing else is written, and the layer keeps no link to
 * the style, so the select always offers a pick rather than showing one.
 */
function BrandStyleRow({ layer, onUpdate }: { layer: BaseLayerData; onUpdate: (prop: string, value: any) => void }) {
  const kit = useAssetKit();
  const { staticMode } = useEditorActions();
  const styles = kit?.typography ?? [];
  if (styles.length === 0) return null;

  const apply = async (index: string) => {
    const style = styles[Number(index)];
    if (!style) return;
    const variants = await styleVariants(style, kit?.fonts ?? []);
    await loadFont(style.family, 400, false);
    onUpdate('fontFamily', style.family);
    // Unknown variants aren't known to be missing, so the weight and italic stay.
    if (variants) resetMissingVariant(layer, variants, onUpdate);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '3px 12px', gap: 6, borderBottom: '1px solid #fafafa', minHeight: 28 }}>
      {!staticMode && <div style={keyframeSlotStyle} />}
      <span style={{ width: 64, fontSize: 10, color: '#64748b', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Brand Style
      </span>
      <select
        aria-label="Brand style"
        value=""
        onChange={(e) => void apply(e.target.value)}
        style={{ ...valueInputStyle, flex: 1, fontFamily: 'inherit', cursor: 'pointer' }}
      >
        <option value="">Apply a style...</option>
        {styles.map((style, i) => (
          <option key={`${style.name}-${i}`} value={i}>
            {style.name} · {style.family}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Bold / Italic buttons ───────────────────────────────

function BoldItalicButtons({ layer, onUpdate }: { layer: BaseLayerData; onUpdate: (prop: string, value: any) => void }) {
  const kit = useAssetKit();
  const brandFonts = kit?.fonts ?? [];
  const googleFamily = getFontFamily(layer.fontFamily);

  // Check brand fonts (from AssetKit) if not found in Google cache
  const fontFamily = googleFamily ?? (() => {
    const matches = brandFonts.filter((f) => f.family === layer.fontFamily);
    if (matches.length === 0) return null;
    const variants = matches.map((f) => ({
      id: 0,
      weight: f.weight ?? 400,
      italic: f.italic ?? false,
      format: 'woff2',
      variable: false,
      axes_json: null as any,
      file_url: f.url,
    }));
    return { family_name: layer.fontFamily, source: 'brand', variants };
  })();

  const currentWeight = layer.fontWeight as number;
  const currentItalic = layer.fontStyle === 'italic';

  // Check variant availability — default to false if we know the font but it lacks variants
  const hasBold = fontFamily
    ? fontFamily.variants.some((v) => v.weight === 700)
    : false;
  const hasItalic = fontFamily
    ? fontFamily.variants.some((v) => v.italic)
    : false;

  const isBold = currentWeight >= 700;

  const handleBold = async () => {
    if (!hasBold && !isBold) return;
    const newWeight = isBold ? 400 : 700;
    await loadFont(layer.fontFamily, newWeight, currentItalic);
    onUpdate('fontWeight', newWeight);
  };

  const handleItalic = async () => {
    if (!hasItalic && !currentItalic) return;
    const newItalic = !currentItalic;
    await loadFont(layer.fontFamily, currentWeight, newItalic);
    onUpdate('fontStyle', newItalic ? 'italic' : 'normal');
  };

  return (
    <div style={{ display: 'flex', gap: 2, flex: 1 }}>
      <button
        onClick={handleBold}
        disabled={!hasBold && !isBold}
        style={{
          ...toggleBtnStyle,
          fontWeight: 700,
          background: isBold ? '#2563eb' : '#f1f5f9',
          color: isBold ? '#fff' : (!hasBold && !isBold) ? '#cbd5e1' : '#475569',
          cursor: (!hasBold && !isBold) ? 'not-allowed' : 'pointer',
        }}
      >
        B
      </button>
      <button
        onClick={handleItalic}
        disabled={!hasItalic && !currentItalic}
        style={{
          ...toggleBtnStyle,
          fontStyle: 'italic',
          background: currentItalic ? '#2563eb' : '#f1f5f9',
          color: currentItalic ? '#fff' : (!hasItalic && !currentItalic) ? '#cbd5e1' : '#475569',
          cursor: (!hasItalic && !currentItalic) ? 'not-allowed' : 'pointer',
        }}
      >
        I
      </button>
    </div>
  );
}

// ─── Property Row (with stopwatch) ────────────────────────

// ─── Color picker with brand swatches ─────────────────────

function ColorPickerWithSwatches({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const kit = useAssetKit();
  const isTransparent = !value || value === 'transparent';

  const swatches: { color: string; tip: string }[] = (kit?.colors ?? []).map((c) => ({
    color: c.value,
    tip: c.name ?? c.value,
  }));

  return (
    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, minWidth: 0 }}>
      <div style={{ position: 'relative', width: 20, height: 20, flexShrink: 0 }}>
        <input
          type="color"
          value={isTransparent ? '#000000' : value}
          onChange={(e) => onChange(e.target.value)}
          title={isTransparent ? 'Transparent' : value}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: 20, height: 20 }}
        />
        {isTransparent ? (
          <div style={{ pointerEvents: 'none' }}>
            <TransparentSwatch selected size={20} onClick={() => {}} />
          </div>
        ) : (
          <div style={{ width: 20, height: 20, borderRadius: 3, border: '1px solid #e2e8f0', background: value, pointerEvents: 'none' }} />
        )}
      </div>
      <TransparentSwatch selected={isTransparent} size={16} onClick={() => onChange('transparent')} />
      {swatches.map(({ color, tip }, i) => (
        <button
          key={i}
          onClick={() => onChange(color)}
          title={tip}
          style={{
            width: 16, height: 16, borderRadius: 3, flexShrink: 0, padding: 0,
            border: value === color ? '2px solid #2563eb' : '1px solid #d1d5db',
            background: color, cursor: 'pointer',
          }}
        />
      ))}
    </div>
  );
}

function TransparentSwatch({ selected, size, onClick }: { selected: boolean; size: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Transparent"
      style={{
        width: size, height: size, borderRadius: 3, flexShrink: 0, padding: 0,
        border: selected ? '2px solid #2563eb' : '1px solid #d1d5db',
        background: '#fff', cursor: 'pointer', position: 'relative', overflow: 'hidden',
      }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: 'absolute', inset: 0 }}>
        <line x1="0" y1={size} x2={size} y2="0" stroke="#ef4444" strokeWidth="1.5" />
      </svg>
    </button>
  );
}

// ─── Media source button ──────────────────────────────────

function MediaSourceButton({ value, layerType, layerId, onUpdate }: { value: string; layerType: string; layerId: string; onUpdate: (src: string) => void }) {
  const [showModal, setShowModal] = useState(false);
  const { transcodeMovForLayer } = useTranscode();
  const fileName = value ? value.split('/').pop()?.split('?')[0] ?? 'Media' : 'Choose...';

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={{
          ...valueInputStyle,
          flex: 1, minWidth: 0,
          color: value ? '#1e293b' : '#94a3b8',
          textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {fileName}
      </button>
      {showModal && (
        <MediaLibraryModal
          fileType={layerType === 'video' ? 'video' : 'image'}
          onSelect={(media) => {
            if (media.storage_uri) onUpdate(media.storage_uri);
            setShowModal(false);
          }}
          onMovUpload={layerType === 'video' ? (media) => {
            const name = media.name || 'Video';
            transcodeMovForLayer(layerId, media.storage_uri!, name);
            setShowModal(false);
          } : undefined}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// ─── Reset Dimensions button ──────────────────────────────

function ResetDimensionsButton({ layer, onUpdate }: { layer: BaseLayerData; onUpdate: (prop: string, value: any) => void }) {
  const [resetting, setResetting] = useState(false);
  const editor = useEditor();

  const handleReset = async () => {
    const src = layer.src as string;
    if (!src || resetting) return;

    setResetting(true);
    try {
      if (layer.type === 'image') {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            editor.batch(() => {
              onUpdate('width', img.naturalWidth);
              onUpdate('height', img.naturalHeight);
              onUpdate('anchorX', img.naturalWidth / 2);
              onUpdate('anchorY', img.naturalHeight / 2);
            });
            resolve();
          };
          img.onerror = () => reject();
          img.src = src;
        });
      } else if (layer.type === 'video') {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        await new Promise<void>((resolve, reject) => {
          vid.onloadedmetadata = () => {
            editor.batch(() => {
              onUpdate('width', vid.videoWidth);
              onUpdate('height', vid.videoHeight);
              onUpdate('anchorX', vid.videoWidth / 2);
              onUpdate('anchorY', vid.videoHeight / 2);
            });
            resolve();
          };
          vid.onerror = () => reject();
          vid.src = src;
        });
      }
    } catch {
      // couldn't load media — ignore
    }
    setResetting(false);
  };

  return (
    <button
      onClick={handleReset}
      disabled={resetting || !layer.src}
      style={{
        width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4,
        color: resetting ? '#94a3b8' : '#64748b', padding: '4px 8px', fontSize: 11,
        cursor: resetting || !layer.src ? 'default' : 'pointer',
      }}
    >
      {resetting ? 'Resetting...' : 'Reset Dimensions'}
    </button>
  );
}

function PropertyRow({
  group,
  layer,
  resolved,
  onUpdate,
  propNames,
  isKeyframed,
  onEnableKeyframing,
  onDisableKeyframing,
  onAddKeyframe,
  onRemoveKeyframe,
  isOnKeyframe,
  onPrevKeyframe,
  onNextKeyframe,
  hasPrevKeyframe,
  hasNextKeyframe,
}: {
  group: PropGroup;
  layer: BaseLayerData;
  resolved: BaseLayerData | null;
  onUpdate: (prop: string, value: any) => void;
  propNames: string[];
  isKeyframed: boolean;
  onEnableKeyframing: (propNames: string[]) => void;
  onDisableKeyframing: (propNames: string[]) => void;
  onAddKeyframe: (propNames: string[]) => void;
  onRemoveKeyframe: (propNames: string[]) => void;
  isOnKeyframe: boolean;
  onPrevKeyframe: () => void;
  onNextKeyframe: () => void;
  hasPrevKeyframe: boolean;
  hasNextKeyframe: boolean;
}) {
  const { staticMode } = useEditorActions();
  const { enabled: adTokensOn } = useAdTokenFindings();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '3px 12px',
      gap: 6,
      borderBottom: '1px solid #fafafa',
      minHeight: 28,
    }}>
      {/* Keyframe controls — fixed-width slot keeps labels aligned across rows */}
      {!staticMode && (
        <div style={keyframeSlotStyle}>
          {isKeyframed ? (
            <KeyframeNavCluster
              onPrev={onPrevKeyframe}
              onAdd={(e) => {
                if (e.metaKey || e.ctrlKey) onDisableKeyframing(propNames);
                else if (isOnKeyframe) onRemoveKeyframe(propNames);
                else onAddKeyframe(propNames);
              }}
              onNext={onNextKeyframe}
              hasPrev={hasPrevKeyframe}
              hasNext={hasNextKeyframe}
              isOnKeyframe={isOnKeyframe}
            />
          ) : (
            <button
              onClick={() => onEnableKeyframing(propNames)}
              title="Enable keyframing"
              style={stopwatchBtnStyle(false)}
            >
              <StopwatchIcon />
            </button>
          )}
        </div>
      )}

      {/* Property label */}
      <span style={{
        width: 64,
        fontSize: 10,
        color: '#64748b',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {group.label}
      </span>

      {/* Value inputs */}
      <div style={{ flex: 1, display: 'flex', gap: 4 }}>
        {group.props.map((prop) => {
          const baseVal = (layer as any)[prop.name];
          const resolvedVal = resolved ? (resolved as any)[prop.name] : baseVal;
          const isAnimated = resolved && resolvedVal !== baseVal;
          const isColor = typeof baseVal === 'string' && (baseVal.startsWith('#') || baseVal === 'transparent' || baseVal === '');
          const isText = typeof baseVal === 'string' && !isColor;

          // Hidden — stopwatch only, no value input
          if (prop.type === 'hidden') {
            return <div key={prop.name} style={{ flex: 1 }} />;
          }

          // Font picker — resets weight/style if new font doesn't support them
          if (prop.type === 'font') {
            return (
              <FontPicker
                key={prop.name}
                value={baseVal ?? ''}
                onChange={(familyName, family) => {
                  onUpdate(prop.name, familyName);
                  resetMissingVariant(layer, family.variants, onUpdate);
                }}
              />
            );
          }

          // Bold / Italic toggles
          if (prop.type === 'bolditalic') {
            return <BoldItalicButtons key={prop.name} layer={layer} onUpdate={onUpdate} />;
          }

          // Explicit color picker
          if (prop.type === 'color') {
            return (
              <ColorPickerWithSwatches
                key={prop.name}
                value={baseVal ?? 'transparent'}
                onChange={(v) => onUpdate(prop.name, v)}
              />
            );
          }

          // Media source button
          if (prop.type === 'media') {
            return <MediaSourceButton key={prop.name} value={baseVal} layerType={layer.type} layerId={layer.id} onUpdate={(v) => onUpdate(prop.name, v)} />;
          }

          // Reset dimensions button
          if (prop.type === 'resetDimensions') {
            return <ResetDimensionsButton key={prop.name} layer={layer} onUpdate={onUpdate} />;
          }

          // A text layer's text: the ad token picker, highlights and notes (MPN-1933)
          if (prop.name === 'text' && layer.type === 'text') {
            return (
              <div key={prop.name} style={{ flex: 1, minWidth: 0 }}>
                {/* The text the composition shows now -- a keyframe's value, if it is
                    keyframed -- not the base text underneath (MPN-1965). Plain with the
                    Studio's ad tokens flagged off (MPN-2014). */}
                {adTokensOn ? (
                  <TextLayerField layer={layer} value={String(resolvedVal ?? '')} onChange={(v) => onUpdate(prop.name, v)} />
                ) : (
                  <input
                    value={String(resolvedVal ?? '')}
                    onChange={(e) => onUpdate(prop.name, e.target.value)}
                    placeholder={prop.label || undefined}
                    style={valueInputStyle}
                  />
                )}
              </div>
            );
          }

          // Boolean toggle
          if (prop.type === 'boolean') {
            const checked = !!baseVal;
            return (
              <button
                key={prop.name}
                onClick={() => onUpdate(prop.name, !checked)}
                style={{
                  flex: 1,
                  padding: '3px 8px',
                  fontSize: 10,
                  background: checked ? '#2563eb' : '#f1f5f9',
                  color: checked ? '#fff' : '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {checked ? 'On' : 'Off'}
              </button>
            );
          }

          return (
            <div key={prop.name} style={{ flex: 1 }}>
              {prop.options ? (
                <div style={{ display: 'flex', gap: 1 }}>
                  {prop.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => onUpdate(prop.name, opt)}
                      style={{
                        flex: 1,
                        padding: '3px 4px',
                        fontSize: 10,
                        background: baseVal === opt ? '#2563eb' : '#f1f5f9',
                        color: baseVal === opt ? '#fff' : '#475569',
                        border: '1px solid #e2e8f0',
                        borderRadius: 3,
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : isColor ? (
                <ColorPickerWithSwatches
                  value={baseVal ?? '#000000'}
                  onChange={(v) => onUpdate(prop.name, v)}
                />
              ) : isText ? (
                <input
                  value={baseVal ?? ''}
                  onChange={(e) => onUpdate(prop.name, e.target.value)}
                  placeholder={prop.label || undefined}
                  style={valueInputStyle}
                />
              ) : (
                <input
                  type="number"
                  value={typeof resolvedVal === 'number' ? (Number.isInteger(resolvedVal) ? resolvedVal : parseFloat(resolvedVal.toFixed(2))) : 0}
                  onChange={(e) => onUpdate(prop.name, parseFloat(e.target.value) || 0)}
                  step={prop.step ?? 1}
                  placeholder={prop.label || undefined}
                  style={{
                    ...valueInputStyle,
                    color: isAnimated ? '#2563eb' : '#1e293b',
                    fontWeight: isAnimated ? 600 : 400,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Ad token fields (MPN-1933) ───────────────────────────

/** A jump from the drawer lands here once the layer and playhead it set have rendered. */
function useAdTokenFocusRequest(
  layerId: string,
  field: AdTokenFocusRequest['field'],
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
) {
  const { focusRequest, clearFocusRequest } = useAdTokenFindings();
  useEffect(() => {
    const input = inputRef.current;
    if (!focusRequest || focusRequest.layerId !== layerId || focusRequest.field !== field || !input) return;
    input.focus();
    input.setSelectionRange(focusRequest.start, focusRequest.end);
    clearFocusRequest();
  }, [focusRequest, layerId, field, inputRef, clearFocusRequest]);
}

/** The layer's name, with the ad token picker on `[`. Not highlighted and no notes here. */
function LayerNameField({ layer, onChange }: { layer: BaseLayerData; onChange: (v: string) => void }) {
  const { tokens } = useAdTokenFindings();
  return (
    <AdTokenInput
      aria-label="Layer name"
      highlight={false}
      tokens={tokens}
      value={layer.name}
      onValueChange={onChange}
      style={{ ...valueInputStyle, fontWeight: 600, fontSize: 13 }}
    />
  );
}


function TextLayerField({ layer, value, onChange }: { layer: BaseLayerData; value: string; onChange: (v: string) => void }) {
  const { tokens } = useAdTokenFindings();
  const handle = useRef<AdTokenFieldHandle>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // A jump from the drawer lands here once the layer and playhead it set have rendered.
  useAdTokenFocusRequest(layer.id, 'text', inputRef);

  return (
    <>
      <AdTokenInput
        ref={handle}
        inputRef={inputRef}
        aria-label="Text"
        tokens={tokens}
        value={value}
        onValueChange={onChange}
        style={valueInputStyle}
      />
      {adTokenSources(layer).map((source) => (
        <TextSourceTokenNotes key={source.keyframeTime ?? 'base'} layer={layer} source={source} handle={handle} />
      ))}
    </>
  );
}

/**
 * The notes for one run of a text layer's text: its base text, or one keyframe's value with
 * the time it shows at. A base-text fix goes through the input like a keystroke; a keyframe's
 * goes to that keyframe, which the input may not be showing.
 */
function TextSourceTokenNotes({ layer, source, handle }: {
  layer: BaseLayerData;
  source: AdTokenSource;
  handle: React.RefObject<AdTokenFieldHandle>;
}) {
  const editor = useEditor();
  const { frameRate } = usePlayhead();
  const check = useAdTokenCheck(source.text);
  if (check.notes.length === 0) return null;
  return (
    <div style={{ fontSize: 10, marginTop: 2, lineHeight: 1.4 }}>
      {source.keyframeTime !== null && (
        <span style={{ color: '#64748b' }}>At {formatTimecode(layer.startTime + source.keyframeTime, frameRate)}</span>
      )}
      <AdTokenMessages
        notes={check.notes}
        noteStyle={{ display: 'block' }}
        onUseCanonical={(note) => {
          if (source.keyframeTime === null) handle.current?.edit((current) => check.applyAliasFix(note, current));
          else fixAdTokenAlias(editor, check.tokens, layer.id, { field: 'text', keyframeTime: source.keyframeTime, normalized: note.normalized });
        }}
      />
    </div>
  );
}

// ─── Keyframe controls ───────────────────────────────────

function StopwatchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
      <circle cx="7" cy="8" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 5v3.5l2 1.5" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M7 1v2" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M5 1.5h4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function stopwatchBtnStyle(active: boolean): React.CSSProperties {
  return {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    width: 16,
    height: 16,
    flexShrink: 0,
    color: active ? '#2563eb' : '#cbd5e1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function KeyframeNavCluster({
  onPrev,
  onAdd,
  onNext,
  hasPrev,
  hasNext,
  isOnKeyframe,
}: {
  onPrev: () => void;
  onAdd: (e: React.MouseEvent) => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  isOnKeyframe: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1 }}>
      <button
        onClick={onPrev}
        disabled={!hasPrev}
        title="Previous keyframe"
        style={navBtnStyle(hasPrev)}
      >
        <svg width="9" height="9" viewBox="0 0 9 9" fill="currentColor">
          <path d="M6.5 1.5L2.5 4.5l4 3z" />
        </svg>
      </button>
      <button
        onClick={onAdd}
        title={isOnKeyframe
          ? 'Remove keyframe at current time (⌘-click to remove all keyframing)'
          : 'Add keyframe at current time (⌘-click to remove keyframing)'}
        style={diamondBtnStyle}
      >
        <svg width="10" height="10" viewBox="0 0 10 10">
          <path
            d="M5 1l4 4-4 4-4-4z"
            fill={isOnKeyframe ? '#2563eb' : 'none'}
            stroke="#2563eb"
            strokeWidth="1.2"
          />
        </svg>
      </button>
      <button
        onClick={onNext}
        disabled={!hasNext}
        title="Next keyframe"
        style={navBtnStyle(hasNext)}
      >
        <svg width="9" height="9" viewBox="0 0 9 9" fill="currentColor">
          <path d="M2.5 1.5L6.5 4.5l-4 3z" />
        </svg>
      </button>
    </div>
  );
}

function navBtnStyle(enabled: boolean): React.CSSProperties {
  return {
    background: 'none',
    border: 'none',
    padding: 0,
    width: 14,
    height: 16,
    cursor: enabled ? 'pointer' : 'default',
    color: enabled ? '#475569' : '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

const keyframeSlotStyle: React.CSSProperties = {
  width: 46,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const diamondBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  width: 16,
  height: 16,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// ─── Styles ───────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  flex: 1,
  background: '#ffffff',
  overflowY: 'auto',
  overflowX: 'hidden',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid #e2e8f0',
  fontWeight: 600,
  fontSize: 12,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  display: 'flex',
  alignItems: 'center',
};

const valueInputStyle: React.CSSProperties = {
  width: '100%',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 4,
  color: '#1e293b',
  padding: '3px 5px',
  fontSize: 11,
  fontFamily: 'monospace',
  outline: 'none',
};

const toggleBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: '3px 4px',
  fontSize: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 3,
};
